/**
 * Usage tracking for Admin → Usage: actions are queued and sent about once a minute with the minutes the app was in
 * active use (tab visible, and a click, key or scroll in the last 3 minutes). Never blocks or breaks the page: if a
 * send fails, that batch is dropped.
 */
import { viewAs } from './impersonation';

type Kind = 'page' | 'filter' | 'drill' | 'clickup' | 'saved_view' | 'export';
interface Ev {
  kind: Kind;
  page: string;
  detail?: string;
  at: number;
}

const FLUSH_MS = 60_000;
const IDLE_MS = 3 * 60_000;
let queue: Ev[] = [];
let activeMin = 0;
let lastInput = Date.now();
let installed = false;
let enabled = false;

export function track(kind: Kind, detail?: string): void {
  // A view-as tab is HR looking, not that person using the app: never counted.
  if (!enabled || viewAs.get()) return;
  queue.push({ kind, page: location.pathname, detail: detail?.slice(0, 160), at: Date.now() });
  if (queue.length >= 50) flush();
}

function flush(): void {
  if (viewAs.get()) {
    queue = [];
    activeMin = 0;
    return;
  }
  if (!queue.length && !activeMin) return;
  const body = JSON.stringify({ events: queue.slice(0, 100), activeMin: Math.min(activeMin, 10) });
  queue = [];
  activeMin = 0;
  // Plain fetch (not api()): a 401 here must not sign anyone out, and keepalive lets it finish as the tab closes.
  void fetch('/api/usage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'attendance-pro' },
    body,
    credentials: 'same-origin',
    keepalive: true,
  }).catch(() => undefined);
}

/** The section a click came from: its panel's heading ("People to keep an eye on"), when there is one. */
export function sectionOf(el: Element | null): string {
  const panel = el?.closest('.panel, .cuflags, .kpis, section[aria-label]');
  const h = panel?.querySelector('h2')?.textContent?.trim() || panel?.getAttribute('aria-label') || '';
  return h;
}

/** Start tracking for a signed-in person (call once; stop with the returned function on sign-out). */
export function startUsage(): () => void {
  enabled = true;
  if (installed) return stopUsage;
  installed = true;
  const input = () => (lastInput = Date.now());
  for (const t of ['pointerdown', 'keydown', 'wheel', 'touchstart']) addEventListener(t, input, { passive: true });
  // Links out to ClickUp, and file downloads, from anywhere in the app.
  addEventListener(
    'click',
    (e) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      if (a.href.includes('app.clickup.com')) track('clickup', sectionOf(a));
      else if (a.hasAttribute('download')) track('export', new URL(a.href).pathname);
    },
    true,
  );
  setInterval(() => {
    if (enabled && document.visibilityState === 'visible' && Date.now() - lastInput < IDLE_MS) activeMin++;
    if (enabled) flush();
  }, FLUSH_MS);
  addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flush());
  addEventListener('pagehide', flush);
  return stopUsage;
}

function stopUsage(): void {
  flush();
  enabled = false;
}
